// gauge.tsx — the RESTRUCTURED Garage home, ported from the Claude Design delivery
// "the gauge, honestly read" (project f354caf3).
//
// 🔴 THE FENCE governs every line in this file. Garage may state the ELAPSED FACT
// ("your last oil change was 4,200 mi and 7 months ago") and NEVER a prediction —
// no "due", no "overdue", no "needed", no countdown, no bar filling toward an interval,
// no red/amber verdict on a service row. Source: CLAUDE.md · AI-VALUE.md §refusal-fence ·
// brain.ts · the Rust signal schema. The delivered styles.css encodes it too (see .svc-track).
//
// The a3 restructure this implements (DESIGN.md, VERDICT FAIL):
//   R1 lead with the CAR'S STATE — odometer + this period's cost + elapsed-since-service,
//      not the app-name header and a vehicle picker
//   R2 the ODOMETER is the spine (it is what every other figure is derived from)
//   R3 the vehicle switcher is demoted to a compact pill
//   R4 service history is a real surface: per-kind elapsed fact + the user's OWN interval
//      drawn as a passive reference tick on a shared axis
//   R5 economy gets a real treatment — a line over DISTANCE, not seven flat bars
import React, { useMemo } from "react";
import type { Vehicle, Fillup, Service } from "./store";
import { ServiceMark, UtilMark, resolveServiceKind } from "./marks";

const DAY = 86_400_000;
const fmtNum = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });

/** The current reading = the highest odometer we have ever seen for this vehicle. */
export function currentOdometer(fillups: Fillup[], services: Service[]): number | null {
  const all = [...fillups.map(f => f.odometer), ...services.map(s => s.odometer)].filter(n => n > 0);
  return all.length ? Math.max(...all) : null;
}

/** Most recent service PER KIND — the delivery shows one row per kind, not one global row.
 *  `kind` is free text, so rows are grouped by the resolved art key. */
export interface KindRow {
  key: string; label: string; odometer: number; ts: string;
  distSince: number | null; daysSince: number;
}
export function servicesByKind(services: Service[], odo: number | null): KindRow[] {
  const latest = new Map<string, Service>();
  for (const s of services) {
    const key = resolveServiceKind(s.kind);
    const cur = latest.get(key);
    if (!cur || s.ts > cur.ts) latest.set(key, s);
  }
  return [...latest.entries()].map(([key, s]) => ({
    key,
    label: s.kind || key,
    odometer: s.odometer,
    ts: s.ts,
    distSince: odo != null && s.odometer > 0 ? Math.max(0, odo - s.odometer) : null,
    daysSince: Math.max(0, Math.floor((Date.now() - new Date(s.ts).getTime()) / DAY)),
  })).sort((a, b) => (b.distSince ?? 0) - (a.distSince ?? 0));
}

const elapsedWords = (days: number) => {
  if (days < 31) return `${days} day${days === 1 ? "" : "s"} ago`;
  const mo = Math.round(days / 30.4);
  if (mo < 24) return `${mo} mo ago`;
  return `${Math.round(mo / 12)} yr ago`;
};

/** R1 + R2 — THE ODOMETER READOUT. Replaces the vehicle name-card that used to sit under
 *  a log button. This is the first content block on the screen. */
export function OdoHero({ vehicle, fillups, services, monthCost, costPerDist, onEdit, onAsk }: {
  vehicle: Vehicle; fillups: Fillup[]; services: Service[];
  monthCost: number | null; costPerDist: number | null; onEdit: () => void;
  /** Opens the assistant. It is an INSTRUMENT in this cluster, never a floating pill. */
  onAsk?: () => void;
}) {
  const odo = currentOdometer(fillups, services);
  const unit = vehicle.distance_unit;
  return (
    <section className="hero">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>
          {[vehicle.year || null, vehicle.make, vehicle.model].filter(Boolean).join(" ")}
          {vehicle.plate ? ` · ${vehicle.plate}` : ""}
        </span>
        <button className="btn btn-sm btn-on-hero" onClick={onEdit}>Edit</button>
      </div>

      <div className="row" style={{ gap: "0.45rem", alignItems: "baseline", marginTop: "0.35rem" }}>
        <span className="serif tnum">{odo != null ? fmtNum(odo) : "—"}</span>
        <span className="muted" style={{ fontSize: "var(--fs-sm)" }}>{unit} on the clock</span>
      </div>

      {/* R6 — a horizontal row inside the hero, NOT the old floating 2×2 tile grid */}
      <div className="stat-grid" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))", marginTop: "0.9rem" }}>
        <div className="stat">
          <div className="stat-lbl" style={{ whiteSpace: "nowrap" }}><UtilMark id="cost" size={13} /> Month</div>
          <div className="stat-val tnum">{monthCost != null ? monthCost.toFixed(0) : "—"}</div>
        </div>
        <div className="stat">
          <div className="stat-lbl" style={{ whiteSpace: "nowrap" }}><UtilMark id="distance" size={13} /> Cost / {unit}</div>
          <div className="stat-val tnum">{costPerDist != null ? costPerDist.toFixed(2) : "—"}</div>
        </div>
        <div className="stat">
          <div className="stat-lbl" style={{ whiteSpace: "nowrap" }}><UtilMark id="fuel" size={13} /> Fills · svc</div>
          <div className="stat-val tnum">{fillups.length}<span className="unit">·{services.length}</span></div>
        </div>
      </div>

      {/* 🔴 THE ASSISTANT ENTRY — an instrument in the cluster, not a floating pill. It reads
          like the dials above it (label over value) and sits on the same baseline, so it is part
          of the panel rather than furniture stuck on top of the app. */}
      {onAsk && (
        <button type="button" className="odo-ask" onClick={onAsk} aria-label="Ask the assistant about this vehicle">
          <span className="odo-ask-dial" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none">
              <path d="M3.6 17.4a10 10 0 1 1 16.8 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              <path d="M12 15.2 16.6 9" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
              <circle cx="12" cy="15.9" r="1.5" fill="currentColor" />
            </svg>
          </span>
          <span className="odo-ask-txt">
            <span className="odo-ask-lbl">Ask</span>
            <span className="odo-ask-sub">log · find · narrate</span>
          </span>
        </button>
      )}
    </section>
  );
}

/** R4 — SINCE LAST SERVICE. Elapsed fact per kind, with the user's OWN interval as a passive
 *  reference tick on a shared axis.
 *  🔴 The track NEVER fills toward the tick and the marks NEVER take good/bad/watch colours —
 *  either would read as "due", which the fence forbids. */
export function SinceLastService({ vehicle, services, fillups }: {
  vehicle: Vehicle; services: Service[]; fillups: Fillup[];
}) {
  const odo = currentOdometer(fillups, services);
  const rows = useMemo(() => servicesByKind(services, odo), [services, odo]);
  const interval = vehicle.service_interval_km || 0;   // the user's OWN stated cadence
  const unit = vehicle.distance_unit;

  if (!rows.length) {
    return (
      <section style={{ marginTop: "1rem" }}>
        <div className="eyebrow">Since last service</div>
        <div className="card" style={{ padding: "1rem" }}>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: 0, lineHeight: 1.5 }}>
            No services logged yet. Log one and this reads back how far and how long since.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section style={{ marginTop: "1rem" }}>
      <div className="eyebrow">Since last service</div>
      {rows.map((r) => {
        // The axis runs to whichever is larger — the interval or the distance already elapsed —
        // so a row that has passed the user's own mark simply shows the tick behind the marker.
        // It is a position on a ruler, never a verdict.
        const span = Math.max(interval || 0, r.distSince ?? 0) * 1.15 || 1;
        const markPct = Math.min(100, ((r.distSince ?? 0) / span) * 100);
        const tickPct = interval > 0 ? Math.min(100, (interval / span) * 100) : null;
        return (
          <div className="card" key={r.key} style={{ padding: "0.85rem 0.95rem", marginTop: "0.55rem" }}>
            <div className="row" style={{ gap: "0.6rem", alignItems: "center" }}>
              <span className="tile-icon" style={{ width: 30, height: 30, flexShrink: 0 }}>
                <ServiceMark kind={r.label} size={16} />
              </span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", overflow: "hidden",
                              textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.label}</div>
                <div className="fact-text tnum">
                  {r.distSince != null ? `${fmtNum(r.distSince)} ${unit} · ` : ""}{elapsedWords(r.daysSince)}
                </div>
              </div>
            </div>
            {tickPct != null && (
              <>
                <div className="svc-track" style={{ marginTop: "0.6rem" }} aria-hidden="true">
                  <span className="svc-tick" style={{ left: `${tickPct}%` }} />
                  <span className="svc-mark" style={{ left: `calc(${markPct}% - 4px)` }} />
                </div>
                <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 4 }}>
                  your interval: {fmtNum(interval)} {unit}
                </div>
              </>
            )}
          </div>
        );
      })}
    </section>
  );
}

/** R5 — FUEL ECONOMY AS A LINE OVER DISTANCE. The old treatment was seven flat bars with no
 *  axis; economy is the category's headline metric and drift is the thing worth seeing. */
export function EconomyLine({ vehicle, fillups }: { vehicle: Vehicle; fillups: Fillup[] }) {
  const pts = useMemo(() => {
    const full = [...fillups].filter(f => f.is_full_tank && f.odometer > 0 && f.volume > 0)
      .sort((a, b) => a.odometer - b.odometer);
    const out: { odo: number; eco: number }[] = [];
    for (let i = 1; i < full.length; i++) {
      const d = full[i].odometer - full[i - 1].odometer;
      if (d > 0 && full[i].volume > 0) out.push({ odo: full[i].odometer, eco: d / full[i].volume });
    }
    return out;
  }, [fillups]);

  if (pts.length < 2) return null;
  const W = 300, H = 90, P = 6;
  const xs = pts.map(p => p.odo), ys = pts.map(p => p.eco);
  const x0 = Math.min(...xs), x1 = Math.max(...xs);
  const y0 = Math.min(...ys), y1 = Math.max(...ys);
  const sx = (v: number) => P + ((v - x0) / (x1 - x0 || 1)) * (W - P * 2);
  const sy = (v: number) => H - P - ((v - y0) / (y1 - y0 || 1)) * (H - P * 2);
  const d = pts.map((p, i) => `${i ? "L" : "M"}${sx(p.odo).toFixed(1)},${sy(p.eco).toFixed(1)}`).join(" ");
  const unit = `${vehicle.distance_unit}/${vehicle.volume_unit}`;

  return (
    <section style={{ marginTop: "1rem" }}>
      <div className="card" style={{ padding: "0.95rem" }}>
        <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.5rem" }}>
          <span style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>Fuel economy</span>
          <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>{unit} over distance</span>
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img"
             aria-label={`Fuel economy over distance, ${pts.length} full tanks`}>
          <path d={d} fill="none" stroke="var(--accent)" strokeWidth="1.6"
                strokeLinejoin="round" strokeLinecap="round" />
          {pts.map((p, i) => (
            <circle key={i} cx={sx(p.odo)} cy={sy(p.eco)} r="2.6"
                    fill="var(--bg)" stroke="var(--accent)" strokeWidth="1.4" />
          ))}
        </svg>
        <div className="row tnum" style={{ justifyContent: "space-between", marginTop: 2 }}>
          <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>{fmtNum(x0)}</span>
          <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>{fmtNum(x1)} {vehicle.distance_unit}</span>
        </div>
      </div>
    </section>
  );
}

/** R3 — the vehicle switcher, demoted from a full-width tab row to one compact pill. */
export function VehiclePill({ vehicles, activeId, onSelect, onAdd, controls }: {
  vehicles: Vehicle[]; activeId: string | null;
  onSelect: (id: string) => void; onAdd: () => void;
  /** Backup + account. This app has NO wordmark header, and they must NOT sit in the odometer
   *  cluster either — that reimports the sibling "two circles top-right" pattern into the one
   *  place that was working. They ride out here on the vehicle row instead. */
  controls?: React.ReactNode;
}) {
  const active = vehicles.find(v => v.id === activeId) ?? vehicles[0];
  const [open, setOpen] = React.useState(false);
  if (!active) return null;
  return (
    <div style={{ position: "relative", marginBottom: "0.85rem", display: "flex",
                  alignItems: "center", justifyContent: "space-between", gap: "0.5rem" }}>
      <button className="btn btn-sm btn-pill" onClick={() => setOpen(o => !o)}
              aria-expanded={open} aria-label="Switch vehicle">
        <strong>{active.name || active.model || "Vehicle"}</strong>
        <span aria-hidden="true" style={{ marginLeft: 6, opacity: 0.7 }}>⌄</span>
      </button>
      {controls && <div className="row" style={{ gap: "0.4rem", alignItems: "center" }}>{controls}</div>}
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div className="card" style={{ position: "absolute", left: 0, top: "calc(100% + 6px)",
                                         zIndex: 50, minWidth: 200, padding: "0.4rem" }}>
            {vehicles.map(v => (
              <button key={v.id} className="seg-item" style={{ width: "100%", textAlign: "left" }}
                      onClick={() => { onSelect(v.id); setOpen(false); }}>
                {v.name || `${v.make} ${v.model}`.trim()}
              </button>
            ))}
            <button className="seg-add" style={{ width: "100%", textAlign: "left" }}
                    onClick={() => { setOpen(false); onAdd(); }}>+ Add vehicle</button>
          </div>
        </>
      )}
    </div>
  );
}
